import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';
import { downsizeImage } from '../lib/imageDownsize';

/** One person's completion of one ask. `evidenceTier` is never rendered away — see §7. */
export interface MyCompletion {
  id: string;
  subjectPersonId: string;
  subjectName: string;
  /** Artifact | SelfAttested. A scan and a tick are different kinds of proof. */
  evidenceTier: string;
  completedAt: string;
  completedByPersonId: string;
}

export interface Requirement {
  id: string;
  /** Document | ExternalLink | Acknowledge. Three kinds, fixed behaviour. */
  kind: string;
  title: string;
  body: string | null;
  externalUrl: string | null;
  /**
   * The hostname the link actually goes to. **Render it beside the link, always.** A link posted by
   * a unit member and put in front of every family, asking them to enter personal details
   * somewhere else, is the exact pattern phishing training says to distrust — the mitigation is
   * that the destination is visible rather than hidden behind the label.
   */
  externalHost: string | null;
  appliesTo: string;
  isRequired: boolean;
  dueAt: string | null;
  sortOrder: number;
  /**
   * The **blank** form to download and print, or null when the ask hands nothing out.
   *
   * Not the returned one — that is {@link ReturnedForm}, and the two have opposite visibility. This
   * is a handout anybody who may read the event may print; a returned slip is the narrowest
   * visibility in the product.
   */
  blankForm: BlankForm | null;
  /** This caller's own answers, and their children's. Never anybody else's. */
  myCompletions: MyCompletion[];
}

/** The blank form an ask hands out. Signed and short-lived, like every URL the product issues. */
export interface BlankForm {
  storedFileId: string;
  url: string;
  contentType: string;
  byteSize: number;
  /** Refresh against this rather than discovering the expiry as a dead link. */
  expiresAt: string;
}

/** One row of the grid. Adult officers and admins only. */
export interface CompletionRow {
  subjectPersonId: string;
  displayName: string;
  classification: string;
  completionId: string | null;
  evidenceTier: string | null;
  completedAt: string | null;
  completedByName: string | null;
}

/**
 * The three counts, kept apart.
 *
 * **Never add any two of these together.** A scan is evidence anybody entitled to it can open; a
 * leader's word that the paper is in their binder is weaker and cannot be checked from anywhere
 * else; a family's tick is weaker still and the product cannot verify it at all. A leader glancing
 * at one merged number before a whitewater trip is making a safety decision on the difference. §7
 * is explicit, and it is very hard to take back once leaders trust a single figure.
 */
export interface CompletionSummary {
  expected: number;
  artifacts: number;
  /** Forms a leader has recorded holding on paper (TROOP-130). Never folded into `artifacts`. */
  physicalCopies: number;
  selfAttested: number;
}

/** One person a returned slip covers, and the completion row that says so. */
export interface EvidenceSubject {
  personId: string;
  displayName: string;
  completionId: string;
}

/**
 * A returned permission slip — TROOP-127.
 *
 * The fields are named to satisfy `LightboxItem` structurally, so the mini gallery reuses
 * `PhotoLightbox` rather than growing a second full-screen viewer.
 */
export interface ReturnedForm {
  storedFileId: string;
  requirementId: string;
  requirementTitle: string;
  /**
   * Everybody this one photograph settles the ask for. More than one when a household returned a
   * single slip with several names on it — which is why the card names them rather than assuming
   * one form is one child.
   */
  subjects: EvidenceSubject[];
  enteredByPersonId: string;
  enteredByName: string;
  completedAt: string;
  url: string;
  thumbnailUrl: string | null;
  /** Short-lived. Refresh against this rather than discovering it as a broken image. */
  expiresAt: string;
  /** Asked per row, because it is a policy answer per row. Never frozen for the page. */
  canWithdraw: boolean;
}

/** What an event asks of one of this parent's children, across every unit they are in. */
export interface FamilyRequirement {
  requirementId: string;
  eventId: string;
  eventTitle: string;
  unitName: string;
  kind: string;
  title: string;
  body: string | null;
  externalUrl: string | null;
  externalHost: string | null;
  dueAt: string | null;
  childPersonId: string;
  childName: string;
  completionId: string | null;
  evidenceTier: string | null;
  completedAt: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const requirementsApi = {
  /**
   * What the event asks, with this caller's own family's answers against each.
   *
   * `unitId` absent picks the guardian's door — the same two-door switch photos and transport use.
   * A parent had no per-event view of this at all before TROOP-116: their only path was
   * {@link forMyFamily}, which answers "what does anybody want from us this term" rather than
   * "what does Saturday need", so the surface a permission slip will land on did not exist on the
   * screen the slip belongs to.
   */
  forEvent: (unitId: string | undefined, eventId: string) =>
    call<Requirement[]>(
      unitId
        ? `/units/${unitId}/events/${eventId}/requirements/`
        : `/family/events/${eventId}/requirements/`,
    ),

  add: (
    unitId: string,
    eventId: string,
    input: {
      kind: string;
      title: string;
      body?: string | null;
      externalUrl?: string | null;
      appliesTo?: string;
      dueAt?: string | null;
    },
  ) =>
    call<Requirement>(`/units/${unitId}/events/${eventId}/requirements/`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Hangs the blank form on an ask — the empty PDF a family downloads, prints and fills in.
   *
   * Its own call rather than a field on {@link add}, so a leader can attach one to an ask that
   * already exists. That is the ordinary case: the council's PDF usually turns up after somebody
   * has already put the campout in the calendar.
   *
   * A photographed form is downsized first, for the reason every other upload here is. A PDF is
   * sent as delivered — there is nothing to decode, and nothing is stripped from it (§6 says so
   * outright, and it is a real limit rather than an oversight).
   */
  attachForm: async (unitId: string, eventId: string, requirementId: string, file: File) => {
    const form = new FormData();

    if (file.type.startsWith('image/')) {
      const { blob } = await downsizeImage(file);
      form.append('file', blob, file.name);
    } else {
      form.append('file', file, file.name);
    }

    // No Content-Type header: the browser sets it, and it has to carry the multipart boundary it
    // generated.
    const response = await fetch(
      `${API_BASE_URL}/units/${unitId}/events/${eventId}/requirements/${requirementId}/form`,
      { method: 'PUT', credentials: 'include', body: form },
    );

    if (!response.ok) {
      const problem = await response.json().catch(() => null);

      throw new ApiError(
        response.status,
        problem?.message ?? problem?.detail ?? 'That form could not be attached.',
      );
    }

    return (await response.json()) as { storedFileId: string };
  },

  detachForm: (unitId: string, eventId: string, requirementId: string) =>
    call<void>(`/units/${unitId}/events/${eventId}/requirements/${requirementId}/form`, {
      method: 'DELETE',
    }),

  /** Everyone the ask applies to, done or not. Adult officers and admins only. */
  grid: (unitId: string, eventId: string, requirementId: string) =>
    call<CompletionRow[]>(
      `/units/${unitId}/events/${eventId}/requirements/${requirementId}/completions`,
    ),

  /**
   * The server's own aggregate of the completion grid, and no longer what any screen reads —
   * TROOP-138.
   *
   * The one place the counts are rendered is the setup block inside `EveryoneTab`, which already
   * holds every row this aggregates — so it derives them with `summariseRoster` instead. Fetching
   * them separately is what let a leader's tick move the grid and leave the numbers behind, and
   * reaching for this again from a component that also mutates completions would put the bug back.
   * Kept because it is the server's definition and `smoke-requirements.ps1` is what proves the
   * derivation still agrees with it.
   */
  summary: (unitId: string, eventId: string, requirementId: string) =>
    call<CompletionSummary>(
      `/units/${unitId}/events/${eventId}/requirements/${requirementId}/summary`,
    ),

  complete: (
    unitId: string,
    eventId: string,
    requirementId: string,
    input: { subjectPersonId?: string | null; evidenceTier?: string } = {},
  ) =>
    call<MyCompletion>(
      `/units/${unitId}/events/${eventId}/requirements/${requirementId}/completions`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  withdraw: (unitId: string, eventId: string, requirementId: string, completionId: string) =>
    call<void>(
      `/units/${unitId}/events/${eventId}/requirements/${requirementId}/completions/${completionId}`,
      { method: 'DELETE' },
    ),

  /**
   * Takes a completion back through whichever door the caller has.
   *
   * The guardian's half arrived with TROOP-127: a parent who photographed the wrong side of the
   * paper has something to undo, and `/family` is a front door rather than a read-only annex. Who
   * may is the server's answer either way — the subject, their guardian, or an adult officer.
   */
  withdrawHere: (
    unitId: string | undefined,
    eventId: string,
    requirementId: string,
    completionId: string,
  ) =>
    call<void>(
      unitId
        ? `/units/${unitId}/events/${eventId}/requirements/${requirementId}/completions/${completionId}`
        : `/family/events/${eventId}/requirements/completions/${completionId}`,
      { method: 'DELETE' },
    ),

  /**
   * A parent's own list, across every unit their children are in.
   *
   * Not under `/units/` on purpose: a guardian holds no membership, so unit scoping 404s the very
   * people this exists for — the same reason the rest of `/family` lives where it does.
   */
  forMyFamily: () => call<FamilyRequirement[]>('/family/requirements'),

  completeForChild: (requirementId: string, childPersonId: string) =>
    call<{ completionId: string; completedAt: string }>('/family/requirements/completions', {
      method: 'POST',
      body: JSON.stringify({ requirementId, childPersonId }),
    }),

  /**
   * Every returned slip on this event that the caller may see — the mini gallery.
   *
   * A leader gets the whole event's; a parent gets their own family's. That is one route and one
   * judge (`RequirementVisibilityPolicy`), not two shapes — §7 calls this the narrowest visibility
   * in the product, and a second read path is how it stops being narrow.
   */
  returnedForms: (unitId: string | undefined, eventId: string) =>
    call<ReturnedForm[]>(
      unitId
        ? `/units/${unitId}/events/${eventId}/requirements/evidence`
        : `/family/events/${eventId}/requirements/evidence`,
    ),

  /**
   * Photographs a filled-in slip and records the completions it settles, in one call.
   *
   * **The downsize is why this is a function rather than a raw `fetch`.** A slip photographed on a
   * phone is an 8 MB file, and it is being sent from a car park at a meeting; skipping it also
   * silently loses the capture date, because the canvas re-encode is what destroys the EXIF
   * locally. The server strips and re-encodes regardless — nothing here is a security measure.
   *
   * `subjectPersonIds` takes more than one on purpose. A household that filled in one form with
   * both children's names on it photographs it once; the server refuses a set no single person
   * answers for, so this cannot be used to hand one family another family's form.
   */
  returnForm: async (
    unitId: string | undefined,
    eventId: string,
    requirementId: string,
    file: File,
    subjectPersonIds: string[],
  ) => {
    const { blob, capturedAt } = await downsizeImage(file);

    const form = new FormData();
    form.append('file', blob, file.name);
    for (const personId of subjectPersonIds) {
      form.append('subjectPersonIds', personId);
    }
    if (capturedAt) {
      form.append('capturedAt', capturedAt);
    }

    const path = unitId
      ? `/units/${unitId}/events/${eventId}/requirements/${requirementId}/evidence`
      : `/family/events/${eventId}/requirements/${requirementId}/evidence`;

    // No Content-Type header: the browser sets it, and it has to carry the multipart boundary it
    // generated. Setting it by hand produces a body the server cannot parse, and the failure reads
    // as a malformed upload rather than as a wrong header.
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => null);

      if (response.status === 400 && problem?.errors) {
        throw new ValidationFailure(problem.errors as FieldErrors);
      }

      throw new ApiError(
        response.status,
        problem?.message ?? problem?.detail ?? problem?.title ?? 'That form could not be sent.',
      );
    }

    return (await response.json()) as {
      storedFileId: string;
      completions: { completionId: string; subjectPersonId: string }[];
    };
  },
};
